/* ==========================================================================
   Salt + Lue — theme.js
   Vanilla, no framework. Every feature degrades to a working server-rendered
   page if JS never loads. Try-on is NOT in this file; it is dynamically
   imported only when the customer asks for it.
   ========================================================================== */
(function () {
  'use strict';

  var SL = window.SL || {};
  SL.routes = SL.routes || { root: '/' };

  /* ---------------- utilities ---------------- */

  function money(cents) {
    var f = SL.moneyFormat || '${{amount}}';
    return f
      .replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, (cents / 100).toFixed(2).replace('.', ','));
  }

  function track(event, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: event }, payload || {}));
    } catch (e) {}
  }
  SL.track = track;

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /* ---------------- reveal on scroll ---------------- */

  function reveals() {
    var els = document.querySelectorAll('.sl-reveal:not(.is-in)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------------- overlays (drawers + modals) ---------------- */

  var lastFocus = null;

  function openOverlay(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    lastFocus = document.activeElement;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sl-locked');
    var f = el.querySelector('input:not([type=hidden]), button, a[href], select, textarea');
    if (f) setTimeout(function () { f.focus(); }, 120);
    return el;
  }

  function closeOverlay(el) {
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.sl-drawer.is-open, .sl-modal.is-open')) {
      document.body.classList.remove('sl-locked');
    }
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
  }
  SL.openOverlay = openOverlay;
  SL.closeOverlay = closeOverlay;

  function overlays() {
    document.addEventListener('click', function (e) {
      var open = e.target.closest('[data-open]');
      if (open) {
        e.preventDefault();
        openOverlay(open.getAttribute('data-open'));
        if (open.getAttribute('data-open') === 'sl-search') track('search_open');
        return;
      }
      var close = e.target.closest('[data-close]');
      if (close) {
        e.preventDefault();
        closeOverlay(close.closest('.sl-drawer, .sl-modal'));
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        closeOverlay(document.querySelector('.sl-drawer.is-open, .sl-modal.is-open'));
        return;
      }
      if (e.key !== 'Tab') return;
      var open = document.querySelector('.sl-drawer.is-open, .sl-modal.is-open');
      if (!open) return;
      var items = open.querySelectorAll('a[href], button:not([disabled]), input:not([type=hidden]), select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  /* ---------------- header ---------------- */

  function header() {
    var el = document.querySelector('[data-header]');
    if (!el) return;
    // Mega menus close on Escape and when focus leaves the item
    el.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var item = e.target.closest('.sl-nav__item');
      if (item) item.querySelector('.sl-nav__top').focus();
    });
  }

  function announcements() {
    var bar = document.querySelector('[data-announce]');
    if (!bar) return;
    var items = bar.querySelectorAll('.sl-announce__item');
    if (items.length < 2) return;
    var gap = parseInt(bar.getAttribute('data-interval'), 10) || 5000;
    var i = 0;
    setInterval(function () {
      items[i].classList.remove('is-on');
      i = (i + 1) % items.length;
      items[i].classList.add('is-on');
    }, gap);
  }

  /* ---------------- cart ---------------- */

  function setBusy(btn, busy, label) {
    if (!btn) return;
    btn.disabled = busy;
    if (busy) {
      btn.dataset.restore = btn.textContent;
      btn.textContent = SL.strings ? SL.strings.adding : 'Adding…';
    } else {
      btn.textContent = label || btn.dataset.restore || btn.textContent;
    }
  }

  async function refreshCart(opts) {
    opts = opts || {};
    try {
      var res = await fetch(SL.routes.root + '?section_id=cart-drawer');
      var html = await res.text();
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('[data-cart-contents]');
      var cur = document.querySelector('[data-cart-contents]');
      if (fresh && cur) cur.innerHTML = fresh.innerHTML;

      var cart = await (await fetch(SL.routes.root + 'cart.js')).json();
      document.querySelectorAll('[data-cart-count]').forEach(function (n) {
        n.textContent = cart.item_count;
        n.hidden = cart.item_count === 0;
      });
      if (opts.open) openOverlay('sl-cart');
      return cart;
    } catch (err) {
      window.location.href = SL.routes.cart;
    }
  }
  SL.refreshCart = refreshCart;

  async function addToCart(items, btn) {
    setBusy(btn, true);
    try {
      var res = await fetch(SL.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      });
      var data = await res.json();
      if (data.status) throw new Error(data.description || data.message);
      setBusy(btn, false, SL.strings ? SL.strings.added : 'Added');
      setTimeout(function () { setBusy(btn, false); }, 1500);
      items.forEach(function (it) {
        track('add_to_cart', { variant_id: it.id, quantity: it.quantity });
      });
      await refreshCart({ open: true });
      return true;
    } catch (err) {
      setBusy(btn, false);
      var msg = btn && btn.closest('form') && btn.closest('form').querySelector('[data-msg]');
      if (msg) {
        msg.textContent = err.message || 'That one is unavailable right now.';
        msg.className = 'sl-msg sl-msg--err';
      }
      return false;
    }
  }
  SL.addToCart = addToCart;

  function cart() {
    document.addEventListener('submit', async function (e) {
      var form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      e.preventDefault();
      var fd = new FormData(form);
      var btn = form.querySelector('[type=submit]');
      await addToCart([{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) }], btn);
    });

    document.addEventListener('click', async function (e) {
      var ctrl = e.target.closest('[data-line-change]');
      if (!ctrl) return;
      e.preventDefault();
      var wrap = document.querySelector('[data-cart-contents]');
      if (wrap) wrap.style.opacity = '0.5';
      var qty = Number(ctrl.getAttribute('data-line-change'));
      if (qty === 0) track('remove_from_cart', { line: ctrl.getAttribute('data-line') });
      await fetch(SL.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(ctrl.getAttribute('data-line')), quantity: qty })
      });
      await refreshCart();
      var after = document.querySelector('[data-cart-contents]');
      if (after) after.style.opacity = '1';
    });

    document.addEventListener('click', function (e) {
      if (e.target.closest('[name=checkout]')) track('begin_checkout');
    });
  }

  /* ---------------- variant selection (PDP + quick shop) ---------------- */

  function variantForm(root) {
    var json = root.querySelector('[data-variants]');
    if (!json) return;
    var variants = JSON.parse(json.textContent);
    var idInput = root.querySelector('[data-variant-id]');
    var submit = root.querySelector('[data-add-button]');
    var priceEl = root.querySelector('[data-price]');
    var stockEl = root.querySelector('[data-stock]');

    function chosen() {
      var out = [];
      root.querySelectorAll('[data-opt]').forEach(function (input) {
        if (input.checked) out[Number(input.getAttribute('data-opt'))] = input.value;
      });
      return out;
    }

    function update() {
      var sel = chosen();
      var match = variants.find(function (v) {
        return v.options.every(function (o, i) { return o === sel[i]; });
      });

      root.querySelectorAll('[data-opt-label]').forEach(function (label) {
        var input = label.querySelector('input');
        if (!input) return;
        var idx = Number(input.getAttribute('data-opt'));
        var possible = variants.some(function (v) {
          return v.available && v.options.every(function (o, i) {
            return i === idx ? o === input.value : o === sel[i];
          });
        });
        label.classList.toggle('is-out', !possible);
      });

      root.querySelectorAll('[data-opt-current]').forEach(function (el) {
        el.textContent = sel[Number(el.getAttribute('data-opt-current'))] || '';
      });

      if (!match) {
        if (submit) { submit.disabled = true; submit.textContent = SL.strings.unavailable; }
        return;
      }
      if (idInput) idInput.value = match.id;
      if (submit) {
        submit.disabled = !match.available;
        submit.textContent = match.available ? SL.strings.addToCart : SL.strings.soldOut;
      }
      if (priceEl && match.price_html) priceEl.innerHTML = match.price_html;

      if (stockEl) {
        var low = Number(stockEl.getAttribute('data-threshold') || 0);
        var show = low > 0 && match.available && match.inventory_management && match.inventory_quantity > 0 && match.inventory_quantity <= low;
        stockEl.hidden = !show;
        if (show) stockEl.textContent = stockEl.getAttribute('data-template').replace('{{count}}', match.inventory_quantity);
      }

      if (match.featured_media_id) {
        var slide = root.querySelector('[data-media="' + match.featured_media_id + '"]');
        if (slide) slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }

      if (root.hasAttribute('data-pdp')) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);
      }

      // Let try-on know the color changed
      root.dispatchEvent(new CustomEvent('sl:variant', { bubbles: true, detail: match }));
      track('select_item', { variant_id: match.id });
    }

    root.addEventListener('change', function (e) {
      if (e.target.matches('[data-opt]')) update();
    });

    root.querySelectorAll('[data-qty-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var input = root.querySelector('[data-qty-input]');
        if (!input) return;
        var next = Math.max(1, Number(input.value) + Number(btn.getAttribute('data-qty-step')));
        input.value = next;
        var out = root.querySelector('[data-qty-label]');
        if (out) out.textContent = next;
      });
    });

    update();
  }

  function productForms() {
    document.querySelectorAll('[data-product-root]:not([data-bound])').forEach(function (root) {
      root.setAttribute('data-bound', '');
      variantForm(root);
    });

    var sticky = document.querySelector('[data-sticky-buy]');
    var anchor = document.querySelector('[data-add-button]');
    if (sticky && anchor && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          sticky.classList.toggle('is-on', !e.isIntersecting && e.boundingClientRect.top < 0);
        });
      }, { threshold: 0 }).observe(anchor);
    }
  }

  /* ---------------- quick shop ---------------- */

  function quickShop() {
    document.addEventListener('click', async function (e) {
      var btn = e.target.closest('[data-quickshop]');
      if (!btn) return;
      e.preventDefault();
      var url = btn.getAttribute('data-quickshop');
      var modal = document.getElementById('sl-quickshop');
      var body = modal && modal.querySelector('[data-quickshop-body]');
      if (!modal || !body) return;

      body.innerHTML = '<p class="sl-note" style="padding:2rem;text-align:center">Loading…</p>';
      openOverlay('sl-quickshop');
      track('quick_shop_open', { url: url });

      try {
        var res = await fetch(url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=quick-shop');
        var html = await res.text();
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var content = doc.querySelector('[data-quickshop-content]');
        body.innerHTML = content ? content.innerHTML : '';
        productForms();
      } catch (err) {
        body.innerHTML = '<p class="sl-msg sl-msg--err" style="padding:2rem">Could not load this product. <a href="' + url + '">Open the full page</a>.</p>';
      }
    });
  }

  /* ---------------- collection filters (AJAX) ---------------- */

  function collection() {
    var form = document.querySelector('[data-filter-form]');
    var results = document.querySelector('[data-results]');
    if (!results) return;

    async function render(url, push) {
      results.classList.add('is-loading');
      try {
        var res = await fetch(url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + results.getAttribute('data-section'));
        var html = await res.text();
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-results]');
        var facets = doc.querySelector('[data-facets]');
        if (fresh) results.innerHTML = fresh.innerHTML;
        var curFacets = document.querySelector('[data-facets]');
        if (facets && curFacets) curFacets.innerHTML = facets.innerHTML;
        if (push) window.history.pushState({ url: url }, '', url);
        reveals();
      } catch (err) {
        window.location.href = url;
      }
      results.classList.remove('is-loading');
    }

    if (form) {
      form.addEventListener('change', debounce(function () {
        var params = new URLSearchParams(new FormData(form));
        // strip empty values so the URL stays clean and shareable
        Array.from(params.keys()).forEach(function (k) {
          if (!params.get(k)) params.delete(k);
        });
        render(form.getAttribute('action') + '?' + params.toString(), true);
      }, 250));
    }

    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-facet-link], .sl-pagination a');
      if (!link) return;
      e.preventDefault();
      render(link.getAttribute('href'), true);
      if (link.closest('.sl-pagination')) window.scrollTo({ top: results.offsetTop - 120, behavior: 'smooth' });
    });

    var sort = document.querySelector('[data-sort]');
    if (sort) {
      sort.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sort.value);
        url.searchParams.delete('page');
        render(url.toString(), true);
      });
    }

    window.addEventListener('popstate', function () { render(window.location.href, false); });
  }

  /* ---------------- predictive search ---------------- */

  var RECENT_KEY = 'sl:recent-searches';

  function getRecent() {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch (e) { return []; }
  }
  function pushRecent(term) {
    if (!term) return;
    try {
      var list = getRecent().filter(function (t) { return t !== term; });
      list.unshift(term);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 6)));
    } catch (e) {}
  }

  function search() {
    var drawer = document.getElementById('sl-search');
    if (!drawer) return;
    var input = drawer.querySelector('[data-search-input]');
    var out = drawer.querySelector('[data-search-results]');
    var recentWrap = drawer.querySelector('[data-recent]');
    if (!input || !out) return;

    function paintRecent() {
      if (!recentWrap) return;
      var list = getRecent();
      recentWrap.hidden = list.length === 0;
      var row = recentWrap.querySelector('[data-recent-list]');
      if (!row) return;
      row.innerHTML = list.map(function (t) {
        return '<button type="button" data-recent-term="' + t.replace(/"/g, '&quot;') + '">' + t + '</button>';
      }).join('');
    }
    paintRecent();

    drawer.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-recent-term]');
      if (!chip) return;
      input.value = chip.getAttribute('data-recent-term');
      input.dispatchEvent(new Event('input'));
    });

    var run = debounce(async function () {
      var q = input.value.trim();
      if (q.length < 2) { out.innerHTML = ''; if (recentWrap) recentWrap.hidden = getRecent().length === 0; return; }
      if (recentWrap) recentWrap.hidden = true;
      try {
        var url = SL.routes.predictive +
          '?q=' + encodeURIComponent(q) +
          '&resources[type]=product,collection,query,page' +
          '&resources[limit]=6' +
          '&section_id=predictive-search';
        var res = await fetch(url);
        out.innerHTML = await res.text();
      } catch (e) {
        out.innerHTML = '';
      }
    }, 220);

    input.addEventListener('input', run);
    var form = drawer.querySelector('form');
    if (form) form.addEventListener('submit', function () { pushRecent(input.value.trim()); });
  }

  /* ---------------- wishlist (no app required) ---------------- */

  var WISH_KEY = 'sl:wishlist';

  function getWish() {
    try { return JSON.parse(localStorage.getItem(WISH_KEY) || '[]'); } catch (e) { return []; }
  }
  function setWish(list) {
    try { localStorage.setItem(WISH_KEY, JSON.stringify(list)); } catch (e) {}
    document.querySelectorAll('[data-wish-count]').forEach(function (n) {
      n.textContent = list.length;
      n.hidden = list.length === 0;
    });
  }

  function wishlist() {
    var list = getWish();
    setWish(list);
    document.querySelectorAll('[data-wish]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', list.indexOf(btn.getAttribute('data-wish')) > -1 ? 'true' : 'false');
    });

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wish]');
      if (!btn) return;
      e.preventDefault();
      var handle = btn.getAttribute('data-wish');
      var cur = getWish();
      var i = cur.indexOf(handle);
      if (i > -1) { cur.splice(i, 1); btn.setAttribute('aria-pressed', 'false'); }
      else { cur.push(handle); btn.setAttribute('aria-pressed', 'true'); track('add_to_wishlist', { handle: handle }); }
      setWish(cur);
      document.querySelectorAll('[data-wish="' + handle + '"]').forEach(function (other) {
        other.setAttribute('aria-pressed', i > -1 ? 'false' : 'true');
      });
    });
  }

  /* ---------------- recently viewed ---------------- */

  var RV_KEY = 'sl:recently-viewed';

  function recordView() {
    var node = document.querySelector('[data-record-view]');
    if (!node) return;
    var handle = node.getAttribute('data-record-view');
    try {
      var list = JSON.parse(localStorage.getItem(RV_KEY) || '[]').filter(function (h) { return h !== handle; });
      list.unshift(handle);
      localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 12)));
    } catch (e) {}
    track('view_item', { handle: handle });
  }

  async function renderRecentlyViewed() {
    var host = document.querySelector('[data-recently-viewed]');
    if (!host) return;
    var current = host.getAttribute('data-exclude');
    var list = [];
    try { list = JSON.parse(localStorage.getItem(RV_KEY) || '[]'); } catch (e) {}
    list = list.filter(function (h) { return h !== current; }).slice(0, 4);
    if (!list.length) { host.closest('[data-recently-viewed-section]').hidden = true; return; }

    var rail = host.querySelector('[data-rv-rail]');
    for (var i = 0; i < list.length; i++) {
      try {
        var res = await fetch(SL.routes.root + 'products/' + list[i] + '?section_id=product-card');
        if (!res.ok) continue;
        var html = await res.text();
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var card = doc.querySelector('.sl-card');
        if (card) rail.appendChild(card);
      } catch (e) {}
    }
    if (!rail.children.length) host.closest('[data-recently-viewed-section]').hidden = true;
  }

  /* ---------------- build your look ---------------- */

  function builder() {
    var root = document.querySelector('[data-builder]');
    if (!root) return;
    var picks = {};

    function repaint() {
      var total = 0, count = 0;
      Object.keys(picks).forEach(function (slot) {
        if (!picks[slot]) return;
        total += picks[slot].price;
        count += 1;
      });
      var totalEl = root.querySelector('[data-builder-total]');
      var addBtn = root.querySelector('[data-builder-add]');
      if (totalEl) totalEl.textContent = money(total);
      if (addBtn) {
        addBtn.disabled = count === 0;
        addBtn.textContent = count === 0
          ? addBtn.getAttribute('data-empty-label')
          : addBtn.getAttribute('data-label').replace('{{count}}', count);
      }
    }

    root.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-builder-pick]');
      if (thumb) {
        var slot = thumb.closest('[data-slot]').getAttribute('data-slot');
        var id = Number(thumb.getAttribute('data-variant'));
        var already = picks[slot] && picks[slot].id === id;
        root.querySelectorAll('[data-slot="' + slot + '"] [data-builder-pick]').forEach(function (t) {
          t.setAttribute('aria-pressed', 'false');
        });
        if (already) {
          picks[slot] = null;
        } else {
          thumb.setAttribute('aria-pressed', 'true');
          picks[slot] = { id: id, price: Number(thumb.getAttribute('data-price')), title: thumb.getAttribute('data-title') };
        }
        var label = root.querySelector('[data-slot="' + slot + '"] [data-slot-chosen]');
        if (label) label.textContent = picks[slot] ? picks[slot].title : label.getAttribute('data-empty');
        repaint();
        return;
      }

      var add = e.target.closest('[data-builder-add]');
      if (add) {
        var items = Object.keys(picks)
          .filter(function (k) { return picks[k]; })
          .map(function (k) { return { id: picks[k].id, quantity: 1 }; });
        if (!items.length) return;
        track('build_your_look_add', { items: items.length });
        addToCart(items, add);
      }
    });

    repaint();
  }

  /* ---------------- shop the look hotspots ---------------- */

  function shopTheLook() {
    document.querySelectorAll('[data-look]').forEach(function (root) {
      var spots = root.querySelectorAll('[data-hotspot]');
      var items = root.querySelectorAll('[data-look-item]');

      function focusItem(key) {
        items.forEach(function (it) {
          var on = it.getAttribute('data-look-item') === key;
          it.classList.toggle('is-active', on);
          if (on) it.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
        spots.forEach(function (s) {
          s.setAttribute('aria-expanded', s.getAttribute('data-hotspot') === key ? 'true' : 'false');
        });
      }

      spots.forEach(function (s) {
        s.addEventListener('click', function () { focusItem(s.getAttribute('data-hotspot')); });
        s.addEventListener('focus', function () { focusItem(s.getAttribute('data-hotspot')); });
      });
    });
  }

  /* ---------------- try-on loader ---------------- */

  function tryOn() {
    document.addEventListener('click', async function (e) {
      var btn = e.target.closest('[data-tryon]');
      if (!btn) return;
      e.preventDefault();
      btn.disabled = true;
      track('try_on_open', { handle: btn.getAttribute('data-tryon-handle') });
      try {
        if (!document.querySelector('link[data-tryon-css]')) {
          var link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = btn.getAttribute('data-tryon-css');
          link.setAttribute('data-tryon-css', '');
          document.head.appendChild(link);
        }
        var mod = await import(btn.getAttribute('data-tryon-src'));
        await mod.open({
          config: JSON.parse(btn.getAttribute('data-tryon-config') || '{}'),
          trigger: btn
        });
      } catch (err) {
        console.error('Try-on failed to load', err);
        alert('The virtual preview could not load. Please try again.');
      }
      btn.disabled = false;
    });
  }

  /* ---------------- init ---------------- */

  function init() {
    reveals();
    overlays();
    header();
    announcements();
    cart();
    productForms();
    quickShop();
    collection();
    search();
    wishlist();
    builder();
    shopTheLook();
    tryOn();
    recordView();
    renderRecentlyViewed();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // Theme editor re-renders sections
  document.addEventListener('shopify:section:load', function () {
    reveals();
    announcements();
    productForms();
    builder();
    shopTheLook();
  });

  window.SL = Object.assign(SL, { money: money, refreshCart: refreshCart, addToCart: addToCart, track: track });
})();
